/**
 * User-activity capture helper (request 0352 / platform spec 0015 P11).
 *
 * Domain and console call this instead of assembling phrases or calling
 * `rtLogService.activity` ad hoc. Never put field *values* in `attrs`.
 *
 * Telemetry is fail-safe: missing `activity` (older published `@rammetec/core`)
 * or a throw must never abort a successful write.
 */
import { rtLogService, } from '@rammetec/core';
import { entityRegistry } from '@rammetec/data-access';
import { diffForAudit } from '../services/fieldAudit/index.js';
const RECORD_LABEL_MAX = 60;
const ERROR_MSG_MAX = 200;
const CRUD_VERBS = {
    'record.create': 'Criou',
    'record.update': 'Alterou',
    'record.delete': 'Excluiu',
    'record.view': 'Abriu',
};
/** Descriptor `label` for `entity`, or the entity name when unregistered. */
export function entityLabel(entity) {
    try {
        if (entityRegistry.has(entity)) {
            const label = entityRegistry.get(entity)?.label;
            if (typeof label === 'string' && label.trim())
                return label.trim();
        }
    }
    catch {
        // Registry may throw in incomplete test harnesses — fall through.
    }
    return entity;
}
/**
 * Short record identification for activity summaries:
 * `code`, else `description`/`name`, truncated to 60. Empty when none.
 */
export function recordLabel(_entity, record) {
    if (record == null || typeof record !== 'object')
        return '';
    const r = record;
    const raw = r.code ?? r.description ?? r.name;
    if (raw == null || raw === '')
        return '';
    const text = String(raw).trim();
    if (!text)
        return '';
    return text.length > RECORD_LABEL_MAX ? text.slice(0, RECORD_LABEL_MAX) : text;
}
/** Past-tense verb + entity label + optional record id fragment. */
export function crudActivitySummary(action, entity, record) {
    const verb = CRUD_VERBS[action];
    const el = entityLabel(entity);
    const rl = record != null ? recordLabel(entity, record) : '';
    return rl ? `${verb} ${el} ${rl}` : `${verb} ${el}`;
}
/** Short error text for `attrs.error` (no emails / field values). */
export function shortErrorMessage(error) {
    if (error == null)
        return 'erro';
    if (typeof error === 'string') {
        return error.length > ERROR_MSG_MAX ? error.slice(0, ERROR_MSG_MAX) : error;
    }
    if (error instanceof Error) {
        const msg = error.message || error.name || 'erro';
        return msg.length > ERROR_MSG_MAX ? msg.slice(0, ERROR_MSG_MAX) : msg;
    }
    const text = String(error);
    return text.length > ERROR_MSG_MAX ? text.slice(0, ERROR_MSG_MAX) : text;
}
/**
 * Changed field *names* for activity attrs. Never throws — returns `undefined`
 * if diff fails (descriptor miss, etc.) so telemetry cannot abort a write.
 */
export function safeChangedFieldNames(before, payload, entity) {
    try {
        if (before == null || typeof before !== 'object')
            return undefined;
        const changes = diffForAudit(before, (payload ?? {}), entity);
        const fields = changes.map((c) => c.fieldName);
        return fields.length > 0 ? fields : undefined;
    }
    catch {
        return undefined;
    }
}
/**
 * Forwards to `rtLogService.activity` when present.
 * Never throws — older core without `activity` is a no-op.
 */
export function logActivity(input) {
    try {
        // Optional member call keeps `this` bound (core `activity` reads instance state).
        rtLogService.activity?.(input);
    }
    catch {
        // Telemetry must never break the caller.
    }
}
/**
 * CRUD write/view activity with standard Portuguese summary.
 * Entire body is fail-safe (summary / attrs / transport).
 */
export function logCrudActivity(input) {
    try {
        const outcome = input.outcome ?? 'ok';
        const attrs = { ...input.attrs };
        if (input.fields && input.fields.length > 0) {
            attrs.fields = input.fields;
        }
        if (input.batched) {
            attrs.batched = true;
        }
        if (outcome === 'error' && input.error !== undefined) {
            attrs.error = shortErrorMessage(input.error);
        }
        const hasAttrs = Object.keys(attrs).length > 0;
        logActivity({
            action: input.action,
            entity: input.entity,
            entityId: input.entityId,
            outcome,
            durationMs: input.durationMs,
            summary: input.summary ?? crudActivitySummary(input.action, input.entity, input.record),
            attrs: hasAttrs ? attrs : undefined,
        });
    }
    catch {
        // Telemetry must never break the caller.
    }
}
//# sourceMappingURL=activity.js.map